import Icon from '@/components/shared/Icons';
import MobileSearchBar from '@/components/search-bar/MobileSearchBar';
import styles from '@/components/search-bar/SearchBar.styles';
import type { DateRange, Matcher } from 'react-day-picker';
import { type ClipboardEvent, useCallback, SetStateAction, Dispatch, useRef, useState } from 'react';
import GuestRoomInput from '@/components/search-bar/GuestRoomInput';
import dynamic from 'next/dynamic';
import { useDatePickerFocusManager } from '@cvent/carina/components/DatePicker_NEXT/utils';
import { useOnOutsideClick } from '@cvent/carina/hooks/useOnOutsideClick';
import type { InputRefs } from '@/components/search-bar/SearchBarContainer';

const DatePicker = dynamic(() => import('@/components/DateRange'), { ssr: false });

export type GuestRoomDetails = {
  checkInDate: Date | undefined;
  checkOutDate: Date | undefined;
  onGuestRoomDateChange: (checkInDate: Date | undefined, checkOutDate: Date | undefined) => void;
};

export type SearchBarFilter = {
  inputType: string;
  id: string;
  name?: string;
  label: string;
  icon: 'map-pin' | 'bed' | 'users' | 'calendar';
  className?: string;
  inputValue?: string | number;
  maxRange?: number;
  isRequired?: boolean;
  errorMessage?: string;
  showErrorMessage?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick?: any;
  ref?: React.RefObject<HTMLInputElement>;
  blockedDays?: Matcher[];
  guestRoomDetails?: GuestRoomDetails;
  onPaste?: (event: ClipboardEvent<HTMLInputElement>) => void;
  onKeyDown?: (event) => void;
  onFocus?: () => void;
  onBlur?: () => void;
};

export type InputProps = {
  type: string;
  id: string;
  name?: string;
  value?: string | number;
  onChange: (event: React.ChangeEvent<HTMLInputElement> | number, isSetFromPopper?: boolean) => void;
  className?: string;
  'aria-label': string;
  placeholder: string;
  ref?: React.RefObject<HTMLInputElement>;
  max?: number;
  min?: number;
  onPaste?: (event: ClipboardEvent<HTMLInputElement>) => void;
  onKeyDown?: (event) => void;
  onFocus?: () => void;
  onBlur?: () => void;
};

export type SearchBarProps = {
  searchBarFilters: SearchBarFilter[];
  labels: Record<string, string>;
  selectedRange?: DateRange;
  handleRangeSelect: (range: DateRange) => void;
  locale: Locale;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  submitHandler: (e: any) => Promise<void>;
  setUserModifiedGRDate: Dispatch<SetStateAction<boolean>>;
  inputRefs: InputRefs;
  isDatePickerOpen: boolean;
  setIsDatePickerOpen: (b: boolean) => void;
  isGuestRoomDatePickerOpen: boolean;
  setIsGuestRoomDatePickerOpen: (b: boolean) => void;
};

const EMPTY_STRING = '';
const getTestId = (id: string): string => {
  let testID = EMPTY_STRING;
  if (id === 'SEARCH_TERM') {
    testID = 'search-bar-search-term';
  } else if (id === 'EVENT_START_DATE') {
    testID = 'search-bar-start-date';
  } else if (id === 'EVENT_END_DATE') {
    testID = 'search-bar-end-date';
  } else if (id === 'NUMBER_OF_ATTENDEES') {
    testID = 'search-bar-number-of-attendees';
  } else {
    testID = 'search-bar-number-of-sleeping-rooms';
  }
  return testID;
};

export default function SearchBar({
  searchBarFilters,
  labels,
  selectedRange,
  handleRangeSelect,
  locale, // date-fns locale
  submitHandler,
  setUserModifiedGRDate,
  inputRefs,
  isDatePickerOpen: isOpen,
  setIsDatePickerOpen: setIsOpen,
  setIsGuestRoomDatePickerOpen,
  isGuestRoomDatePickerOpen
}: SearchBarProps): JSX.Element {
  const { startInputRef, endInputRef } = inputRefs;
  const [focusedInput, setFocusedInput] = useState<'start' | 'end'>('start');
  const isError = searchBarFilters.some(filter => filter.showErrorMessage);
  const popperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLInputElement>(null);

  const handleDismiss = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const { handleCanReturnFocus, setCanReturnFocusRef } = useDatePickerFocusManager(isOpen, handleDismiss);

  const handleStartFocus = useCallback(() => {
    setIsOpen(true);
    setFocusedInput('start');
  }, [setIsOpen]);

  const handleEndFocus = useCallback(() => {
    setIsOpen(true);
    setFocusedInput('end');
  }, [setIsOpen]);

  useOnOutsideClick(
    [popperRef, triggerRef],
    () => {
      setCanReturnFocusRef(true);
      handleDismiss();
    },
    isOpen
  );

  const dateClearButton = useCallback(
    // eslint-disable-next-line arrow-body-style
    (label: string, id: string): JSX.Element => {
      return (
        <div className=" flex w-full scale-90 cursor-pointer justify-end pt-[6px] text-b-base text-neutral-60 lg:pt-[2px]">
          <button
            type="button"
            tabIndex={0}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              if (handleRangeSelect) {
                if (id === 'EVENT_START_DATE') {
                  handleRangeSelect({ from: undefined, to: selectedRange?.to });
                } else {
                  handleRangeSelect({ from: selectedRange?.from, to: undefined });
                }
              }
            }}
            aria-label={label}
          >
            <Icon id="x" testID={`${id}-CLEAR-EVENT`} className="h-6 w-6 fill-neutral-60" />
          </button>
        </div>
      );
    },
    [handleRangeSelect, selectedRange?.from, selectedRange?.to]
  );

  const renderSearchBarFields = useCallback(
    (id: string, inputProps: InputProps, blockedDays: Matcher[] | undefined, guestRoomDetails?: GuestRoomDetails) => {
      if (id === 'NUMBER_OF_SLEEPING_ROOMS') {
        return (
          <GuestRoomInput
            datePickerOpen={isGuestRoomDatePickerOpen}
            setDatePickerOpen={setIsGuestRoomDatePickerOpen}
            triggerRef={inputRefs.guestRoomsInputRef}
            inputProps={inputProps}
            labels={labels}
            blockedDays={blockedDays}
            guestRoomDetails={guestRoomDetails}
            locale={locale}
            setUserModifiedGRDate={setUserModifiedGRDate}
          />
        );
      }
      if (id === 'EVENT_START_DATE') {
        return (
          <>
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
            <div onClick={handleStartFocus} ref={startInputRef}>
              <input
                {...inputProps}
                ref={triggerRef}
                id="start"
                data-cvent-id={getTestId(id)}
                // open calendar on down arrow or space
                onKeyDown={e => {
                  if (e.key === 'Down' || e.key === 'ArrowDown' || e.key === ' ' || e.key === 'Spacebar') {
                    if (!isOpen) {
                      e.preventDefault();
                      handleStartFocus();
                    }
                  }
                }}
                readOnly
                onFocus={() => {
                  startInputRef.current?.classList.add('border', 'border-brand-60');
                }}
                onBlur={() => {
                  startInputRef.current?.classList.remove('border', 'border-brand-60');
                }}
              />
              <label htmlFor={inputProps?.id} className={styles.dateLabel}>
                <span className="flex hidden cursor-pointer lg:block">
                  <p className="truncate pl-7">{labels.startDate}</p>
                </span>

                {selectedRange?.from && dateClearButton(labels.guestRoomPopperResetDatesLabel, 'EVENT_START_DATE')}
              </label>
            </div>
            {isOpen && (
              <DatePicker
                isOpen={isOpen}
                handleRangeSelect={handleRangeSelect}
                selectedRange={selectedRange}
                locale={locale}
                labels={{
                  dialogAriaLabel: labels.dialog,
                  prevButtonAriaLabel: labels.previousMonth,
                  nextButtonAriaLabel: labels.nextMonth
                }}
                triggerRef={inputRefs.startInputRef}
                popperRef={popperRef}
                handleCanReturnFocus={handleCanReturnFocus}
                setCanReturnFocusRef={setCanReturnFocusRef}
                focusedInput={focusedInput}
                setFocusedInput={setFocusedInput}
                handleDismiss={handleDismiss}
              />
            )}
          </>
        );
      }
      if (id === 'EVENT_END_DATE') {
        return (
          <>
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
            <div onClick={handleEndFocus} ref={endInputRef}>
              <input
                {...inputProps}
                id="end"
                data-cvent-id={getTestId(id)}
                onClick={handleEndFocus}
                // open calendar on down arrow or space
                onKeyDown={e => {
                  if (e.key === 'Down' || e.key === 'ArrowDown' || e.key === ' ' || e.key === 'Spacebar') {
                    if (!isOpen) {
                      e.preventDefault();
                      handleEndFocus();
                    }
                  }
                }}
                readOnly
                onFocus={() => {
                  endInputRef.current?.classList.add('border', 'border-brand-60');
                }}
                onBlur={() => {
                  endInputRef.current?.classList.remove('border', 'border-brand-60');
                }}
              />
              <label htmlFor={inputProps?.id} className={styles.dateLabel}>
                <span className="flex hidden cursor-pointer lg:block">
                  <p className="truncate pl-7">{labels.endDate}</p>
                </span>
                {selectedRange?.to && dateClearButton(labels.guestRoomPopperResetDatesLabel, 'EVENT_END_DATE')}
              </label>
            </div>
          </>
        );
      }
      return <input {...inputProps} data-cvent-id={getTestId(id)} />;
    },
    [
      dateClearButton,
      endInputRef,
      focusedInput,
      handleCanReturnFocus,
      handleDismiss,
      handleEndFocus,
      handleRangeSelect,
      handleStartFocus,
      inputRefs.guestRoomsInputRef,
      inputRefs.startInputRef,
      isGuestRoomDatePickerOpen,
      isOpen,
      labels,
      locale,
      selectedRange,
      setCanReturnFocusRef,
      setIsGuestRoomDatePickerOpen,
      setUserModifiedGRDate,
      startInputRef
    ]
  );

  return (
    <>
      <MobileSearchBar
        inputRefs={inputRefs}
        searchBarFilters={searchBarFilters}
        labels={labels}
        selectedRange={selectedRange}
        handleRangeSelect={handleRangeSelect}
        locale={locale}
        submitHandler={submitHandler}
        setUserModifiedGRDate={setUserModifiedGRDate}
        isDatePickerOpen={isOpen}
        setIsDatePickerOpen={setIsOpen}
        isGuestRoomDatePickerOpen={isGuestRoomDatePickerOpen}
        setIsGuestRoomDatePickerOpen={setIsGuestRoomDatePickerOpen}
      />
      <div className={styles.searchBox(isError)}>
        {searchBarFilters.map(filter => {
          const {
            inputType,
            inputValue,
            id,
            icon,
            label,
            name,
            className,
            onChange,
            isRequired,
            ref,
            maxRange,
            showErrorMessage,
            blockedDays,
            guestRoomDetails,
            onPaste,
            onKeyDown,
            onFocus,
            onBlur
          } = filter;
          const inputProps: InputProps = {
            type: inputType,
            id,
            name,
            value: inputValue,
            onChange,
            className: `${className} ${styles.textBox(showErrorMessage, id)}`,
            'aria-label': label,
            placeholder: label,
            ref,
            min: 0,
            max: maxRange,
            onPaste,
            onKeyDown,
            onFocus,
            onBlur
          };

          return (
            <div
              className={`relative w-full flex-1 cursor-pointer ${
                id !== 'SEARCH_TERM' ? 'min-w-[170px] max-w-[240px]' : 'min-w-[240px]'
              }`}
              key={id}
            >
              <div id="searchBoxContainer" className="relative w-full cursor-pointer">
                <div className="absolute p-4 pt-5">
                  <Icon id={icon} className="h-4 w-4 fill-neutral-60" />
                </div>
                {renderSearchBarFields(id, inputProps, blockedDays, guestRoomDetails)}
                {id !== 'EVENT_START_DATE' && id !== 'EVENT_END_DATE' && (
                  <label htmlFor={id} className={`${styles.floatingLabel} ${isRequired ? 'pl-5' : 'pl-7'}`}>
                    <span className="flex">
                      {isRequired ? <div className="h-4 w-2 text-danger-60">*</div> : null}
                      <p className="overflow-hidden text-ellipsis whitespace-nowrap" title={label}>
                        {label}
                      </p>
                    </span>
                  </label>
                )}
              </div>
              {filter.showErrorMessage && filter.errorMessage && id !== 'NUMBER_OF_SLEEPING_ROOMS' && (
                <div className="absolute mt-1 flex items-center">
                  <Icon id="alert-triangle" className="h-4 w-3 fill-danger-60" />
                  <div className={`ml-2 ${id === 'NUMBER_OF_ATTENDEES' ? 'truncate' : ''} text-b-sm text-danger-60`}>
                    {filter.errorMessage}
                  </div>
                </div>
              )}
            </div>
          );
        })}
        <div className="invisible rounded-r-full border border-solid border-neutral-30 p-2 lg:visible">
          {/* submit is not supported by carina, using custom */}
          <button
            type="submit"
            disabled={isError}
            className={`${isError ? 'carina-button__disabled' : 'carina-button'} rounded-3xl bg-brand-60 p-2`}
            aria-label={labels.searchVenues}
            data-cvent-id="search-bar-search-button"
          >
            <Icon id="search" className="h-6 w-6 fill-background" />
          </button>
        </div>
      </div>
    </>
  );
}
